import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import Popover from "components/ui/popover/Popover";
import UIcon from "lib/icons/untitled_ui/UIcon";

const WORKS_WELL_EXAMPLES = [
  '"EV charging infrastructure and installation services"',
  '"Temporary, interim, and contract staffing services"',
  '"Bespoke app & website design & build"',
];

type DoesntWorkExample = {
  text: string;
  rationale: string;
};

const DOESNT_WORK_EXAMPLES: DoesntWorkExample[] = [
  {
    text: '"We help public sector organisations with their digital transformation programmes"',
    rationale: "reads like a company pitch",
  },
  {
    text: '"Cleaning services and IT recruitment"',
    rationale: "two unrelated areas, better as separate searches",
  },
  {
    text: '"AI" or "CRM"',
    rationale: 'too short on their own, try "AI implementation services" or "CRM software"',
  },
];

export function TopicSearchHelperPopover() {
  return (
    <Popover
      trigger={
        <HelpButton aria-label="Learn about search topics">
          <UIcon icon="helpCircle" size={14} />
        </HelpButton>
      }
      usePortal
      side="right"
      align="start"
      contentPadding={16}
      borderRadius={8}
    >
      <PopoverBody column gap={12}>
        <PopoverTitle>What is a search topic?</PopoverTitle>
        <DefinitionText>
          A search topic describes one focus area you&apos;d like to track. We use it to find
          contract notices that match what you do.
        </DefinitionText>
        <Section column gap={6}>
          <SectionHeading>Works well</SectionHeading>
          <SectionSubheading>One focus area, described in a few phrases.</SectionSubheading>
          <ExampleList column gap={4}>
            {WORKS_WELL_EXAMPLES.map((example) => (
              <ExampleRow key={example} alignItems="flex-start" gap={6}>
                <Bullet>•</Bullet>
                <ExampleText>{example}</ExampleText>
              </ExampleRow>
            ))}
          </ExampleList>
        </Section>
        <Section column gap={6}>
          <SectionHeading>Doesn&apos;t work well</SectionHeading>
          <SectionSubheading>
            Full sentences, unrelated areas combined, or very short acronyms.
          </SectionSubheading>
          <ExampleList column gap={4}>
            {DOESNT_WORK_EXAMPLES.map(({ text, rationale }) => (
              <ExampleRow key={text} alignItems="flex-start" gap={6}>
                <Bullet>•</Bullet>
                <span>
                  <ExampleText>{text}</ExampleText>
                  <Rationale> ({rationale})</Rationale>
                </span>
              </ExampleRow>
            ))}
          </ExampleList>
        </Section>
      </PopoverBody>
    </Popover>
  );
}

const HelpButton = styled.button(({ theme }) => ({
  all: "unset",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.colors.sysTextSecondary,
  borderRadius: "50%",
  "&:hover": {
    color: theme.colors.sysPrimaryDefault,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.colors.sysPrimaryDefault}`,
    outlineOffset: 2,
  },
}));

const PopoverBody = styled(Flex)({
  width: 360,
});

const PopoverTitle = styled.p(({ theme }) => ({
  margin: 0,
  fontSize: 14,
  fontWeight: theme.font.fontWeightSemiBold,
  lineHeight: "20px",
  color: theme.colors.sysTextDefault,
}));

const DefinitionText = styled.p(({ theme }) => ({
  margin: 0,
  fontSize: 13,
  lineHeight: "20px",
  color: theme.colors.sysTextDefault,
}));

const Section = styled(Flex)({});

const SectionHeading = styled.p(({ theme }) => ({
  margin: 0,
  fontSize: 12,
  fontWeight: theme.font.fontWeightSemiBold,
  lineHeight: "18px",
  color: theme.colors.sysTextDefault,
}));

const SectionSubheading = styled.p(({ theme }) => ({
  margin: 0,
  fontSize: 12,
  lineHeight: "18px",
  color: theme.colors.sysTextSecondary,
}));

const ExampleList = styled(Flex)({});

const ExampleRow = styled(Flex)({});

const Bullet = styled.span(({ theme }) => ({
  fontSize: 12,
  lineHeight: "18px",
  color: theme.colors.sysTextSecondary,
  flexShrink: 0,
}));

const ExampleText = styled.span(({ theme }) => ({
  fontSize: 12,
  lineHeight: "18px",
  color: theme.colors.sysTextDefault,
}));

const Rationale = styled.em(({ theme }) => ({
  fontSize: 12,
  lineHeight: "18px",
  color: theme.colors.sysTextSecondary,
  fontStyle: "italic",
}));
